import { useQueryClient } from "@tanstack/react-query";

import { useFetchMutation, useFetchQuery, useOptimisticFetchMutation } from "common/queries/hooks";
import { ResourceAvailabilityByResource, ResourceAvailabilityByTime } from "common/types/scheduler";

import {
  AppointmentBulkUpdate,
  AppointmentBulkCreate,
  ClientAppointment,
  ClientAppointmentWithForms,
  AppointmentListSearch,
  CalendarResourceAvailabilityParams,
  CalendarInfo,
  CalendarResourceForClientAppointment,
  AppointmentKindForClientAppointment,
} from "../types";
import { urls, keys } from "./constants";

const noop = () => null;

export const useAppointmentBulkUpdateMutation = ({
  onSuccess,
}: {
  onSuccess?: (appointments: ClientAppointmentWithForms[]) => void;
} = {}) => {
  const queryClient = useQueryClient();

  return useFetchMutation<AppointmentBulkUpdate, ClientAppointmentWithForms[]>({
    fetch: (body) => ({ method: "PATCH", url: urls.bulkAppointments(), body }),
    config: {
      onSuccess: (appointments) => {
        onSuccess?.(appointments);
        queryClient.invalidateQueries(keys.appointmentLists());
      },
    },
  });
};

type AppointmentBulkOptimisticUpdate = {
  body: AppointmentBulkUpdate;
  entities: {
    resource?: CalendarResourceForClientAppointment;
    kind?: AppointmentKindForClientAppointment;
  };
};

export const useAppointmentBulkUpdateOptimisticMutation = () => {
  return useOptimisticFetchMutation<
    AppointmentBulkOptimisticUpdate,
    ClientAppointment[],
    ClientAppointment[],
    AppointmentBulkUpdate
  >({
    fetch: ({ body }) => ({ method: "PATCH", url: urls.bulkAppointments(), body }),
    optimistic: {
      dataKeys: [keys.appointmentLists()],
      update: (oldData, { body, entities }) => {
        return oldData.map((appointment) => {
          if (body.appointment_ids.includes(appointment.id)) {
            return {
              ...appointment,
              ...body,
              resource: entities.resource ?? appointment.resource,
              kind: entities.kind ?? appointment.kind,
            };
          }
          return appointment;
        });
      },
    },
  });
};

export const useAppointmentBulkCreateMutation = ({
  onSuccess = noop,
}: {
  onSuccess?: (resp: ClientAppointment[]) => void;
} = {}) => {
  return useFetchMutation<AppointmentBulkCreate, ClientAppointment[]>({
    fetch: (body) => ({ method: "POST", url: urls.bulkAppointments(), body }),
    config: { onSuccess },
  });
};

export const useAppointmentsQuery = (
  search: AppointmentListSearch,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ClientAppointment[], AppointmentListSearch>(keys.appointmentList(search), {
    fetch: { url: urls.appointments(), body: search },
    config: { enabled },
  });
};

export const useCalendarResourceAvailabilityByResourceQuery = (
  calendarId: number,
  params: CalendarResourceAvailabilityParams,
) => {
  return useFetchQuery<ResourceAvailabilityByResource[], CalendarResourceAvailabilityParams>(
    keys.resourceAvailability(calendarId, params),
    {
      fetch: { url: urls.availabilitiesByResource(calendarId), body: params },
    },
  );
};

export const useCalendarResourceAvailabilityByTimeQuery = (
  calendarId: number,
  params: CalendarResourceAvailabilityParams,
) => {
  return useFetchQuery<ResourceAvailabilityByTime[], CalendarResourceAvailabilityParams>(
    keys.timeAvailability(calendarId, params),
    {
      fetch: { url: urls.availabilitiesByTime(calendarId), body: params },
    },
  );
};

export const useCalendarInfoQuery = (calendarId: number) => {
  return useFetchQuery<CalendarInfo, CalendarResourceAvailabilityParams>(
    keys.calendarInfo(calendarId),
    {
      fetch: { url: urls.calendarInfo(calendarId) },
    },
  );
};
